// Activo Fijo → Registro → Arrendamientos (NIIF 16).
//
// Desde 2019 la NIIF 16 borró la distinción entre arriendo operativo y
// financiero para el arrendatario: casi todo contrato pone en el balance un
// ACTIVO POR DERECHO DE USO y un PASIVO. La cuota deja de ser «gasto de
// arriendo» y se parte en interés y amortización.
//
// El activo que nace aquí lo deprecia el MISMO motor que el resto del módulo.
// Dos motores de depreciación acaban divergiendo siempre.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileSignature, Loader2, Play, Plus, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import {
  ArriendoCuota, ArriendoRow, Campo, Categoria, Modal, Opcion, Switch,
  af, afRpc, fieldCls, listarProveedores, money,
} from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const PERIODICIDAD = [
  { v: 'mensual', l: 'Mensual' }, { v: 'trimestral', l: 'Trimestral' }, { v: 'anual', l: 'Anual' },
];

export function ArrendamientosAF() {
  const qc = useQueryClient();
  const [sel, setSel] = useState<ArriendoRow | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [f, setF] = useState({
    codigo: '', descripcion: '', arrendador_id: '', categoria_id: '',
    fecha_inicio: fechaLocal(), plazo_meses: 36, periodicidad: 'mensual',
    cuota: 0, modalidad: 'anticipada', tasa_anual: 12,
    costos_directos: 0, incentivos: 0, exento: false, motivo_exencion: '',
  });

  const { data: rows = [], isFetching } = useQuery<ArriendoRow[]>({
    queryKey: ['af-arriendos'], queryFn: () => afRpc<ArriendoRow[]>('af_arriendos'),
  });
  const { data: categorias = [] } = useQuery<Categoria[]>({
    queryKey: ['af-categorias-op'],
    queryFn: () => af.list<Categoria>('categorias', '?select=*&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });
  const { data: proveedores = [] } = useQuery<Opcion[]>({ queryKey: ['af-proveedores'], queryFn: listarProveedores });
  const inval = () => { void qc.invalidateQueries({ queryKey: ['af-arriendos'] }); void qc.invalidateQueries({ queryKey: ['af-activos'] }); };

  const guardar = useMutation({
    mutationFn: () => afRpc<{ pasivo_inicial: number }>('af_arriendo_guardar', {
      _data: {
        ...f,
        arrendador_id: f.arrendador_id || null,
        categoria_id: f.categoria_id || null,
        motivo_exencion: f.motivo_exencion || null,
      },
    }),
    onSuccess: (r) => {
      toast.success(f.exento
        ? 'Contrato exento registrado (no genera activo ni pasivo)'
        : `Contrato guardado · pasivo inicial ${money(r.pasivo_inicial)}`);
      setAbierto(false); inval();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (sel) return <PanelArriendo arr={sel} onBack={() => { setSel(null); inval(); }} />;

  // Sirve para que el usuario vea, antes de guardar, que el valor presente es
  // menor que la suma nominal: es la idea entera de la norma.
  const nominal = f.cuota * Math.max(Math.floor(f.plazo_meses / (f.periodicidad === 'mensual' ? 1 : f.periodicidad === 'trimestral' ? 3 : 12)), 1);

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><FileSignature className="h-5 w-5" /> Arrendamientos (NIIF 16)</h1>
        <Button onClick={() => setAbierto(true)}><Plus className="mr-1 h-4 w-4" /> Nuevo contrato</Button>
      </div>
      <p className="max-w-3xl text-sm text-muted-foreground">
        Un contrato de arriendo pone en el balance un <b>activo por derecho de uso</b> y un <b>pasivo</b> por los pagos futuros. La cuota deja de ser gasto de arriendo: se parte en interés y amortización del pasivo, y el activo se deprecia como cualquier otro.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base">Contratos ({rows.length}){isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Contrato</th><th className="py-2 pr-3">Arrendador</th>
              <th className="py-2 pr-3">Inicio</th><th className="py-2 pr-3 text-right">Plazo</th>
              <th className="py-2 pr-3 text-right">Cuota</th><th className="py-2 pr-3 text-right">Pasivo inicial</th>
              <th className="py-2 pr-3 text-right">Saldo</th><th className="py-2 pr-3">Activo</th>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin contratos de arrendamiento</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{r.descripcion}{r.codigo && <div className="font-mono text-xs text-muted-foreground">{r.codigo}</div>}</td>
                  <td className="py-2 pr-3">{r.arrendador ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{r.fecha_inicio}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{r.plazo_meses} m</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.cuota)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.exento ? '—' : money(r.pasivo_inicial ?? 0)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.exento ? '—' : money(r.saldo_pasivo ?? 0)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.placa_activo ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${r.estado === 'vigente' ? 'bg-estable-soft text-estable' : r.estado === 'borrador' ? 'bg-pendiente-soft text-pendiente' : 'bg-muted text-muted-foreground'}`}>{r.estado}</span>
                    {r.exento && <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground" title={r.motivo_exencion ?? 'NIIF 16 §5'}>exento</span>}
                  </td>
                  <td className="py-2 text-right"><Button size="sm" variant="outline" onClick={() => setSel(r)}><Table2 className="mr-1 h-4 w-4" /> Ver</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nuevo contrato de arrendamiento" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Descripción *"><input className={fieldCls} value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} placeholder="Local comercial Av. Amazonas" /></Campo>
              <Campo label="Código interno"><input className={fieldCls} value={f.codigo} onChange={(e) => setF({ ...f, codigo: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Arrendador">
                <select className={fieldCls} value={f.arrendador_id} onChange={(e) => setF({ ...f, arrendador_id: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social ?? p.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Categoría del activo">
                <select className={fieldCls} value={f.categoria_id} onChange={(e) => setF({ ...f, categoria_id: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Inicio *"><input type="date" className={fieldCls} value={f.fecha_inicio} onChange={(e) => setF({ ...f, fecha_inicio: e.target.value })} /></Campo>
              <Campo label="Plazo (meses) *"><input type="number" min="1" className={fieldCls} value={f.plazo_meses} onChange={(e) => setF({ ...f, plazo_meses: Number(e.target.value) })} /></Campo>
              <Campo label="Periodicidad">
                <select className={fieldCls} value={f.periodicidad} onChange={(e) => setF({ ...f, periodicidad: e.target.value })}>
                  {PERIODICIDAD.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Cuota *"><input type="number" step="0.01" min="0" className={fieldCls} value={f.cuota} onChange={(e) => setF({ ...f, cuota: Number(e.target.value) })} /></Campo>
              <Campo label="Modalidad">
                <select className={fieldCls} value={f.modalidad} onChange={(e) => setF({ ...f, modalidad: e.target.value })}>
                  <option value="anticipada">Anticipada (se paga al inicio)</option>
                  <option value="vencida">Vencida (se paga al final)</option>
                </select>
              </Campo>
              <Campo label="Tasa anual % *"><input type="number" step="0.01" min="0" className={fieldCls} value={f.tasa_anual} onChange={(e) => setF({ ...f, tasa_anual: Number(e.target.value) })} /></Campo>
            </div>
            <p className="text-xs text-muted-foreground">
              La tasa es la <b>incremental de endeudamiento</b>: a qué tasa le prestarían a la empresa para comprar ese activo. Es lo que convierte {money(nominal)} de cuotas futuras en su valor de hoy.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Costos directos iniciales"><input type="number" step="0.01" min="0" className={fieldCls} value={f.costos_directos} onChange={(e) => setF({ ...f, costos_directos: Number(e.target.value) })} /></Campo>
              <Campo label="Incentivos recibidos"><input type="number" step="0.01" min="0" className={fieldCls} value={f.incentivos} onChange={(e) => setF({ ...f, incentivos: Number(e.target.value) })} /></Campo>
            </div>

            <div className="rounded-md border p-3">
              <Switch checked={f.exento} onChange={(v: boolean) => setF({ ...f, exento: v })}
                label="Exento (NIIF 16 §5): corto plazo o activo de bajo valor" />
              {f.exento && (
                <>
                  <Campo label="Motivo de la exención">
                    <input className={`${fieldCls} mt-2`} value={f.motivo_exencion}
                      onChange={(e) => setF({ ...f, motivo_exencion: e.target.value })}
                      placeholder="Plazo menor a 12 meses / activo de bajo valor" />
                  </Campo>
                  <p className="mt-1 text-xs text-muted-foreground">
                    No genera activo ni pasivo: la cuota va a gasto. El contrato queda registrado igual, porque hay que revelarlo en notas.
                  </p>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending}
                disabled={!f.descripcion.trim() || f.cuota <= 0 || f.plazo_meses <= 0}
                onClick={() => guardar.mutate()}>Guardar y calcular</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------- la tabla del contrato ----
function PanelArriendo({ arr, onBack }: { arr: ArriendoRow; onBack: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuotas = [], isFetching } = useQuery<ArriendoCuota[]>({
    queryKey: ['af-arr-tabla', arr.id],
    queryFn: () => afRpc<ArriendoCuota[]>('af_arriendo_tabla', { _arr_id: arr.id }),
  });
  const inval = () => {
    void qc.invalidateQueries({ queryKey: ['af-arr-tabla', arr.id] });
    void qc.invalidateQueries({ queryKey: ['af-arriendos'] });
  };

  const activar = useMutation({
    mutationFn: () => afRpc<{ placa?: string; exento?: boolean; aviso?: string; contabilizado?: boolean }>('af_arriendo_activar', { _arr_id: arr.id }),
    onSuccess: (r) => {
      if (r.exento) toast.info(r.aviso ?? 'Contrato exento');
      else if (r.contabilizado) toast.success(`Activo por derecho de uso creado · ${r.placa}`);
      else toast.warning(`Activo creado SIN asiento: ${r.aviso ?? 'revisa Mapeo contable'}`);
      inval(); onBack();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const devengar = useMutation({
    mutationFn: (id: string) => afRpc('af_arriendo_devengar', { _cuota_id: id }),
    onSuccess: () => { toast.success('Cuota devengada'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const proxima = cuotas.find((c) => !c.devengada_at);
  const totInt = cuotas.reduce((a, c) => a + Number(c.interes), 0);

  return (
    <div className="space-y-4 p-6">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
        {arr.estado === 'borrador' && (
          <Button size="sm" onClick={async () => { if (await dlg.confirmar('¿Activar el contrato?', { detalle: 'Se crea el activo por derecho de uso y entra el pasivo al balance.' })) activar.mutate(); }}>
            <Play className="mr-1 h-4 w-4" /> Activar contrato
          </Button>
        )}
      </div>

      <h1 className="text-lg font-semibold">
        {arr.descripcion}
        <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${arr.estado === 'vigente' ? 'bg-estable-soft text-estable' : arr.estado === 'borrador' ? 'bg-pendiente-soft text-pendiente' : 'bg-muted text-muted-foreground'}`}>{arr.estado}</span>
      </h1>

      {arr.exento ? (
        <p className="rounded-md border bg-muted/30 p-3 text-sm">
          Contrato <b>exento</b> (NIIF 16 §5){arr.motivo_exencion ? `: ${arr.motivo_exencion}` : ''}. No genera activo ni pasivo; la cuota va directo a gasto. Queda registrado para revelarlo en notas.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Dato l="Cuotas nominales" v={money(arr.cuota * arr.n_cuotas)} />
          <Dato l="Pasivo inicial (valor presente)" v={money(arr.pasivo_inicial ?? 0)} fuerte />
          <Dato l="Interés total del contrato" v={money(totInt)} />
          <Dato l="Saldo del pasivo" v={money(arr.saldo_pasivo ?? 0)} fuerte />
        </div>
      )}

      {!arr.exento && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              Tabla de amortización ({arr.devengadas}/{arr.n_cuotas} devengadas)
              {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3 text-right">Saldo inicial</th><th className="py-2 pr-3 text-right">Cuota</th>
                <th className="py-2 pr-3 text-right">Interés</th><th className="py-2 pr-3 text-right">Amortización</th>
                <th className="py-2 pr-3 text-right">Saldo final</th><th className="py-2 pr-3">Asiento</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {cuotas.map((c) => (
                  <tr key={c.id} className={`border-b last:border-0 ${c.devengada_at ? 'text-muted-foreground' : ''} ${proxima?.id === c.id ? 'bg-primary/5' : ''}`}>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{c.numero}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{c.fecha}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(c.saldo_inicial)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(c.cuota)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(c.interes)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(c.amortizacion)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(c.saldo_final)}</td>
                    <td className="py-1.5 pr-3 text-xs">{c.asiento_numero ? `#${c.asiento_numero}` : '—'}</td>
                    <td className="py-1.5 text-right">
                      {arr.estado === 'vigente' && !c.devengada_at && proxima?.id === c.id && (
                        <Button size="sm" variant="outline" isLoading={devengar.isPending} onClick={() => devengar.mutate(c.id)}>Devengar</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-muted-foreground">
              Las cuotas se devengan <b>en orden</b>: el pasivo se amortiza cuota a cuota, y saltarse una lo dejaría mal. La depreciación del activo por derecho de uso va por la corrida mensual normal, no por aquí.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Dato({ l, v, fuerte }: { l: string; v: string; fuerte?: boolean }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{l}</div>
      <div className={`font-mono tabular-nums ${fuerte ? 'text-lg font-semibold' : ''}`}>{v}</div>
    </div>
  );
}
